import { getAuthenticityToken, translate, valueFromTarget } from "../helpers";
import ApplicationController from "./application_controller";

const REFLEX_TIMEOUT_MS = 3000;

const removeErrorFeedback = (target) => {
  target.classList.remove("is-invalid");
  const feedback = target.nextElementSibling;
  if (feedback && feedback.classList.contains("invalid-feedback")) {
    feedback.remove();
  }
};

function addErrorsFeedback(target, errors) {
  target.classList.add("is-invalid");
  let feedback = target.nextElementSibling;
  if (!(feedback && feedback.classList.contains("invalid-feedback"))) {
    feedback = document.createElement("div");
    feedback.classList.add("invalid-feedback");
    target.insertAdjacentElement("afterend", feedback);
  }
  feedback.innerHTML = errors.join("<br>");
}

export default class extends ApplicationController {
  static values = { signedGid: String, updateUrl: String };
  // Stores: reflexId => { target: HTMLElement, timer: number }
  pendingReflexIds = new Map();

  startReflexTimeout(reflexId, target) {
    const timer = window.setTimeout(() => this.onReflexTimeout(reflexId), REFLEX_TIMEOUT_MS);
    this.pendingReflexIds.set(reflexId, { target, timer });
  }

  // Clear watchdog timer if set
  clearReflexTimeout(reflexId) {
    const rec = this.pendingReflexIds.get(reflexId);
    if (rec && rec.timer) window.clearTimeout(rec.timer);
  }

  onReflexTimeout(reflexId) {
    const rec = this.pendingReflexIds.get(reflexId);
    if (!rec) return;
    addErrorsFeedback(rec.target, [translate("controllers.form_controller.timeout")]);
    document.body.classList.remove("stimulus-loading");
    this.pendingReflexIds.delete(reflexId);
  }

  beforeReflex() {
    document.body.classList.add("stimulus-loading");
  }

  afterReflex(element, reflex, noop, reflexId) {
    super.afterReflex(element, reflex, noop, reflexId);
    document.body.classList.remove("stimulus-loading");
    if (!this.pendingReflexIds.has(reflexId)) return;
    const { target } = this.pendingReflexIds.get(reflexId);
    this.clearReflexTimeout(reflexId);
    removeErrorFeedback(target);
    this.pendingReflexIds.delete(reflexId);
  }

  reflexError(_element, _reflex, error, reflexId) {
    if (!this.pendingReflexIds.has(reflexId)) return;
    const { target } = this.pendingReflexIds.get(reflexId);
    this.clearReflexTimeout(reflexId);
    addErrorsFeedback(target, [error || translate("controllers.form_controller.field_not_updated")]);
    this.pendingReflexIds.delete(reflexId);
  }

  // TODO Refactor this using a proper way to refresh the form
  // See https://docs.stimulusreflex.com/guide/working-with-forms.html
  update(event) {
    event.preventDefault();
    const target = event.target;

    const value = valueFromTarget(target);
    const signedGid = this.signedGidValue;
    const attribute = target.getAttribute("data-attribute");
    const refresh = target.getAttribute("data-refresh");

    if (!this.isActionCableConnectionOpen()) {
      addErrorsFeedback(target, [translate("controllers.form_controller.no_connection")]);
      return;
    }

    const reflex = this.stimulate("FormReflex#update", { serializeForm: false }, signedGid, attribute, value, refresh);

    if (reflex && reflex.reflexId) {
      this.startReflexTimeout(reflex.reflexId, target);
    }
  }

  /**
   * Update the attribute with validation feedback from the server
   * Add a bootstrap error message if the update fails
   * Remove the bootstrap error message if the update succeeds
   */
  async updateWithValidation(event) {
    event.preventDefault();
    const target = event.target;
    const value = valueFromTarget(target);
    const attribute = target.getAttribute("data-attribute");
    const body = JSON.stringify({ attribute, value });

    try {
      const res = await fetch(this.updateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getAuthenticityToken(),
        },
        body,
      });
      const { success, errors } = await res.json();
      if (success) {
        removeErrorFeedback(target);
      } else {
        addErrorsFeedback(target, errors);
      }
    } catch (e) {
      console.error("Update with validation failed", e);
      addErrorsFeedback(target, [translate("controllers.form_controller.field_not_updated")]);
    }
  }
}
